<script>
import { debounce, uniqBy } from 'lodash';
import { GlCollapsibleListbox, GlFormGroup } from '@gitlab/ui';
import produce from 'immer';
import getGroupProjects from 'ee/security_dashboard/graphql/queries/get_group_projects.query.graphql';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { s__ } from '~/locale';

export default {
  components: {
    GlCollapsibleListbox,
    GlFormGroup,
  },
  inject: {
    fullPath: {
      required: true,
    },
  },
  apollo: {
    projects: {
      query: getGroupProjects,
      variables() {
        return {
          fullPath: this.fullPath,
          search: this.searchTerm,
        };
      },
      update(data) {
        /**
         * It is important to preserve all projects that has been loaded
         * otherwise after performing backend search and selecting found item
         * selection is overwritten
         */
        const { projects: { nodes = [] } = {} } = data.group || {};
        return uniqBy([...this.projects, ...nodes], 'id');
      },
      result({ data }) {
        this.pageInfo = data?.group?.projects?.pageInfo || {};
      },
    },
  },
  props: {
    value: {
      type: String,
      required: false,
      default: null,
    },
    valid: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      projects: [],
      searchTerm: '',
      pageInfo: {},
    };
  },
  computed: {
    loading() {
      return this.$apollo.queries.projects.loading;
    },
    toggleText() {
      const selectedProject = this.projects.find((project) => project.id === this.value);
      return selectedProject?.name || s__('SecurityReports|Select project');
    },
    filteredProjects() {
      return this.projects.filter((project) =>
        project.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    },
    hasNextPage() {
      return this.pageInfo.hasNextPage;
    },
    items() {
      return this.filteredProjects.map((project) => ({ value: project.id, text: project.name }));
    },
    // Returns true to show search loading state only when searching initially.
    // Excludes infinite scroll loading (`hasNextPage`) to prevent search loading and
    // infinite scroll loading from both appearing.
    searching() {
      return this.loading && this.searchTerm !== '' && !this.hasNextPage;
    },
  },
  created() {
    this.debouncedSearch = debounce(this.setSearchTerm, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
  },
  destroyed() {
    this.debouncedSearch.cancel();
  },
  methods: {
    setSearchTerm(term = '') {
      this.searchTerm = term.trim();
    },
    loadMore() {
      this.$apollo.queries.projects.fetchMore({
        variables: {
          after: this.pageInfo.endCursor,
        },
        updateQuery(previousResult, { fetchMoreResult }) {
          return produce(fetchMoreResult, (draftData) => {
            draftData.group.projects.nodes.unshift(...previousResult.group.projects.nodes);
          });
        },
      });
    },
  },
};
</script>

<template>
  <gl-form-group
    label-class="gl-hidden"
    :state="valid"
    :invalid-feedback="s__('SecurityReports|This selection is required.')"
    class="gl-mb-0"
    data-testid="project-form-group"
  >
    <gl-collapsible-listbox
      :selected="value"
      searchable
      :items="items"
      :variant="valid ? 'default' : 'danger'"
      :category="valid ? 'primary' : 'secondary'"
      :infinite-scroll="hasNextPage"
      :infinite-scroll-loading="loading"
      :loading="loading && !searching"
      :searching="searching"
      :toggle-text="toggleText"
      :aria-invalid="!valid"
      @bottom-reached="loadMore"
      @search="debouncedSearch"
      @select="$emit('input', $event)"
    />
  </gl-form-group>
</template>
